import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface DocNumberingConfig {
  prefix: string
  fiscalYear: string
  startingNumber: number
  currentCounter: number
  numberFormat: string
  padding: number
  isActive: boolean
}

export interface DocumentNumberingItem {
  id: string
  organizationId: string
  name: string
  quotation: DocNumberingConfig
  proforma: DocNumberingConfig
  taxInvoice: DocNumberingConfig
  runningBill?: DocNumberingConfig
  estimateInvoice?: DocNumberingConfig
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

export interface DocumentNumberingFilters {
  isActive?: boolean
  search?: string
}

export function useDocumentNumbering(filters?: DocumentNumberingFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['documentNumbering', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/document-numbering?${params}`)
      return (res?.data || []) as DocumentNumberingItem[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useDocumentNumberingMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['documentNumbering', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<DocumentNumberingItem, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/document-numbering', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<DocumentNumberingItem> }) =>
      api.patch<any>(`/master-data/document-numbering/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/document-numbering/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/document-numbering/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove }
}
